// @ts-check
// =============================================================================
// js/ui/art-picker.js — the fullscreen artwork picker.
//
// Opened from the art-count button under a card in the gallery or the deck
// builder's pool. The card's artworks are GROUPED BY ARTIST (the style's
// `credit`), each group titled with the artist's name and its owned/total
// count, and sorted inside by RARITY (drop chance: common first, rarest
// last). Owned artworks render as rounded tiles (artwork alone, no card
// frame); styles the player hasn't unlocked yet show a "?" placeholder so
// the missing pieces are visible. Tap an artwork to select it; PRESS-HOLD to
// blow it up to the full screen (card name above, artist below); release
// dismisses. Close, or a click outside the sheet, dismisses the picker.
// =============================================================================

import { gen, isArtAllowed } from '../render/card-tile.js';
import { escapeHtml } from './modal.js';
import { rememberArt } from './art-prefs.js';
import { artImgAttrs } from '../net/cdn.js';

/** How long a press must hold before the artwork expands (ms). */
const HOLD_MS = 300;

/** newarts fields that are metadata — every OTHER string field is a file. */
const STYLE_META_KEYS = new Set([
	'art_id', 'credit', 'chance', 'flavor', 'safe', 'nude', 'vore', 'gore', 'smut', 'notes',
]);

/** @type {HTMLElement|null} the open overlay (one at a time) */
let openEl = null;

// Navigating away closes the picker, like modals.
window.addEventListener('hashchange', () => {
	closeArtZoom();
	closeArtPicker();
});

/** Close the picker (no-op when closed). */
export function closeArtPicker() {
	openEl?.remove();
	openEl = null;
}

/**
 * Fold a card's art metadata + owned files into artist groups.
 * @param {any} card  A catalog card (newarts + collectedArts).
 * @returns {Array<{artist: string, ownedCount: number, total: number,
 *   styles: Array<{chance: number, owned: string[], locked: boolean}>}>}
 */
function buildArtistGroups(card) {
	const collected = new Set(card.collectedArts || []);
	const matched = new Set();
	/** @type {Map<string, any[]>} artist -> styles */
	const byArtist = new Map();

	for (const style of card.newarts || []) {
		const files = Object.entries(style)
			.filter(([key, value]) => !STYLE_META_KEYS.has(key) && typeof value === 'string' && value)
			.map(([, value]) => value);
		if (!files.length) continue;
		const owned = files.filter((file) => collected.has(file));
		owned.forEach((file) => matched.add(file));
		// A LOCKED style only earns its "?" tile if the player could actually
		// see it: at least one variant must pass their content + gender
		// filters (strict — no fail-open teasing of filtered-out art).
		if (!owned.length && !files.some((file) => isArtAllowed(card, file, true))) {
			continue;
		}
		const artist = String(style.credit || 'Unknown');
		if (!byArtist.has(artist)) byArtist.set(artist, []);
		byArtist.get(artist).push({ chance: Number(style.chance) || 0, owned, locked: !owned.length });
	}
	// Owned files with no matching style (legacy data): artist from the
	// filename's third token, shown as their own unlocked entries.
	for (const file of collected) {
		if (matched.has(file)) continue;
		const artist = String(file).split('_')[2] || 'Unknown';
		if (!byArtist.has(artist)) byArtist.set(artist, []);
		byArtist.get(artist).push({ chance: 50, owned: [file], locked: false });
	}

	return [...byArtist.entries()]
		.map(([artist, styles]) => ({
			artist,
			// Rarity order: common (high drop chance) first, rarest last;
			// chance 0 (special/promo) at the very end.
			styles: styles.sort((a, b) => (b.chance || -1) - (a.chance || -1)),
			ownedCount: styles.filter((s) => !s.locked).length,
			total: styles.length,
		}))
		.sort((a, b) => a.artist.localeCompare(b.artist));
}

/**
 * Open the fullscreen art picker for a card.
 * @param {any} card                       A catalog card (has collectedArts).
 * @param {(art: number) => void} [onPick] Called after selectedArt changes.
 * @returns {void}
 */
export function openArtPicker(card, onPick) {
	closeArtPicker();
	const groups = buildArtistGroups(card);
	if (!groups.length) return;
	const storageBase = gen?.config?.storageBase || 'https://storage.googleapis.com/deckbiter';

	const overlay = document.createElement('div');
	overlay.className = 'art-picker-overlay';
	overlay.innerHTML = `
		<div class="art-picker">
			<div class="art-picker-head">
				<span class="art-picker-title">${escapeHtml(card.name)} — pick an artwork</span>
				<button class="defaultButton js-close">Close</button>
			</div>
			<div class="art-picker-scroll"></div>
		</div>`;
	document.body.appendChild(overlay);
	openEl = overlay;

	overlay.addEventListener('click', (event) => {
		if (event.target === overlay) closeArtPicker();
	});
	overlay.querySelector('.js-close')?.addEventListener('click', () => closeArtPicker());

	const scroll = overlay.querySelector('.art-picker-scroll');
	for (const group of groups) {
		const title = document.createElement('div');
		title.className = 'art-group-title';
		title.textContent = `${group.artist} (${group.ownedCount}/${group.total})`;
		scroll?.appendChild(title);

		const grid = document.createElement('div');
		grid.className = 'art-picker-grid';
		scroll?.appendChild(grid);

		for (const style of group.styles) {
			if (style.locked) {
				const locked = document.createElement('div');
				locked.className = 'art-picker-slot art-picker-locked';
				locked.title = 'More artwork to unlock!';
				locked.innerHTML = `<span>?</span>`;
				grid.appendChild(locked);
				continue;
			}
			for (const file of style.owned) {
				grid.appendChild(buildArtTile(card, file, `${storageBase}/${file}`, group.artist, onPick));
			}
		}
	}
}

/**
 * One owned-artwork tile: tap selects, press-hold blows up.
 * @param {any} card
 * @param {string} file
 * @param {string} url
 * @param {string} artist
 * @param {((art: number) => void)|undefined} onPick
 * @returns {HTMLElement}
 */
function buildArtTile(card, file, url, artist, onPick) {
	const slot = document.createElement('div');
	slot.className = 'art-picker-slot' + (file === card.selectedArt ? ' is-current' : '');
	slot.innerHTML = `<img class="art-picker-art" ${artImgAttrs(url)}
		alt="${escapeHtml(card.name)}" loading="lazy" draggable="false">`;

	let holdTimer = 0;
	let heldOpen = false;
	const startHold = () => {
		heldOpen = false;
		clearTimeout(holdTimer);
		holdTimer = window.setTimeout(() => {
			heldOpen = true;
			openArtZoom(url, card.name, artist);
		}, HOLD_MS);
	};
	const cancelHold = () => clearTimeout(holdTimer);
	slot.addEventListener('pointerdown', startHold);
	slot.addEventListener('pointerleave', cancelHold);
	slot.addEventListener('pointercancel', cancelHold);
	slot.addEventListener('contextmenu', (e) => e.preventDefault()); // long-press menus
	slot.addEventListener('pointerup', () => {
		cancelHold();
		if (heldOpen) {
			// Hold-to-peek: releasing the press dismisses the blow-up (touch
			// keeps pointer capture on the slot, so close from here too).
			closeArtZoom();
			return;
		}
		card.selectedArt = file;
		rememberArt(card.id, file); // persist the explicit pick across sessions
		closeArtPicker();
		onPick?.(0);
	});
	return slot;
}

// -----------------------------------------------------------------------------
// The artwork blow-up: the raw art at the largest size the screen allows
// (contain — full width or full height, whichever fits), with the card name
// above and the artist credit below. Tap to dismiss.
// -----------------------------------------------------------------------------

/** @type {HTMLElement|null} */
let zoomEl = null;

function closeArtZoom() {
	zoomEl?.remove();
	zoomEl = null;
}

/**
 * @param {string} url
 * @param {string} [name]    Card name (shown above).
 * @param {string} [artist]  Artist credit (shown below).
 */
function openArtZoom(url, name = '', artist = '') {
	closeArtZoom();
	const zoom = document.createElement('div');
	zoom.className = 'art-zoom-overlay';
	zoom.innerHTML = `
		<div class="art-zoom-name">${escapeHtml(name)}</div>
		<img src="${escapeHtml(url)}" alt="${escapeHtml(name)}" draggable="false">
		<div class="art-zoom-credit">${artist ? 'Art by ' + escapeHtml(artist) : ''}</div>`;
	zoom.addEventListener('click', () => closeArtZoom());
	zoom.addEventListener('pointerup', () => closeArtZoom());
	document.body.appendChild(zoom);
	zoomEl = zoom;
}
